import { useEffect, useMemo, useState } from "react";
import api, { API_BASE, formatApiError } from "@/lib/api";
import { Plus, Pencil, Trash2, QrCode, Download, Copy, ExternalLink } from "lucide-react";
import { Modal, Field } from "@/pages/ClientsPage";
import { useAuth } from "@/lib/auth";
import WeekdayPicker from "@/components/WeekdayPicker";

const empty = { location_id: "", name: "", area_type: "", cleaning_frequency: "", instructions: "", photo_required: false, status: "active", expected_per_day: 3, working_days: [0, 1, 2, 3, 4] };

const AREA_TYPES = ["Baño hombres","Baño mujeres","Cocina","Recepción","Oficina","Pasillo","Sala de juntas","Comedor","Área común","Entrada","Bodega","Elevadores","Otro"];

export default function AreasPage() {
  const { user } = useAuth();
  const readOnly = user?.role === "cliente";
  const [items, setItems] = useState([]);
  const [locations, setLocations] = useState([]);
  const [clients, setClients] = useState([]);
  const [filterLocation, setFilterLocation] = useState("");
  const [filterClient, setFilterClient] = useState("");
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [err, setErr] = useState("");
  const [saving, setSaving] = useState(false);
  const [qrModal, setQrModal] = useState(null); // area object

  const load = async () => {
    setLoading(true);
    const params = {};
    if (filterLocation) params.location_id = filterLocation;
    else if (filterClient) params.client_id = filterClient;
    const [{ data: areas }, { data: locs }, { data: cls }] = await Promise.all([
      api.get("/areas", { params }),
      api.get("/locations"),
      api.get("/clients"),
    ]);
    setItems(areas); setLocations(locs); setClients(cls); setLoading(false);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filterLocation, filterClient]);

  const locMap = useMemo(() => Object.fromEntries(locations.map(l => [l.id, l])), [locations]);
  const cliMap = useMemo(() => Object.fromEntries(clients.map(c => [c.id, c.name])), [clients]);

  const filteredLocations = useMemo(() => filterClient ? locations.filter(l => l.client_id === filterClient) : locations, [locations, filterClient]);

  const openNew = () => { setEditing(null); setForm({ ...empty, location_id: filterLocation || (locations[0]?.id || "") }); setErr(""); setModalOpen(true); };
  const openEdit = (it) => { setEditing(it); setForm({ ...empty, ...it }); setErr(""); setModalOpen(true); };
  const close = () => setModalOpen(false);

  const save = async (e) => {
    e.preventDefault(); setErr(""); setSaving(true);
    try {
      if (!form.location_id) throw { response: { data: { detail: "Selecciona una sucursal" } } };
      if (editing) await api.put(`/areas/${editing.id}`, form);
      else await api.post("/areas", form);
      close(); await load();
    } catch (e) { setErr(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("¿Eliminar área?")) return;
    await api.delete(`/areas/${id}`);
    await load();
  };

  const showQR = (a) => setQrModal(a);

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="lx-overline">Puntos de limpieza</div>
          <h1 className="lx-section-title mt-1">{readOnly ? "Mis áreas" : "Áreas / Códigos QR"}</h1>
          <p className="text-lx-muted text-sm mt-1">{readOnly ? "Áreas registradas en tus sucursales." : "Cada área tiene un QR único que el trabajador escanea para registrar la limpieza."}</p>
        </div>
        <div className="flex flex-wrap gap-2 items-end">
          {!readOnly && (
            <div>
              <label className="lx-label">Cliente</label>
              <select className="lx-input min-w-[170px]" value={filterClient} onChange={(e) => { setFilterClient(e.target.value); setFilterLocation(""); }} data-testid="filter-area-client">
                <option value="">Todos</option>
                {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
          )}
          <div>
            <label className="lx-label">Sucursal</label>
            <select className="lx-input min-w-[170px]" value={filterLocation} onChange={(e) => setFilterLocation(e.target.value)} data-testid="filter-area-location">
              <option value="">Todas</option>
              {filteredLocations.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
            </select>
          </div>
          {!readOnly && (
            <button className="lx-btn lx-btn-primary h-[42px]" onClick={openNew} data-testid="new-area-button">
              <Plus size={16}/> Nueva área
            </button>
          )}
        </div>
      </div>

      <div className="lx-card p-0 overflow-hidden">
        {loading ? <div className="p-6 text-lx-muted">Cargando…</div> : items.length === 0 ? (
          <div className="p-10 text-center text-lx-muted">
            <p>{readOnly ? "Aún no hay áreas registradas en tus sucursales." : "No hay áreas registradas."}</p>
            {!readOnly && <button className="lx-btn lx-btn-primary mt-4" onClick={openNew}>Crear la primera</button>}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="lx-table">
              <thead>
                <tr><th>Área</th><th>Sucursal</th><th>Cliente</th><th>Tipo</th><th>Frecuencia</th><th>Estatus</th><th className="text-right">Acciones</th></tr>
              </thead>
              <tbody>
                {items.map((a) => {
                  const loc = locMap[a.location_id];
                  const wd = a.working_days || [0,1,2,3,4];
                  const wdStr = wd.length === 7 ? "Todos los días" : wd.length === 5 && wd.join(",") === "0,1,2,3,4" ? "L-V" : wd.map(d => ["L","M","M","J","V","S","D"][d]).join("·");
                  const epd = a.expected_per_day ?? 3;
                  return (
                    <tr key={a.id} data-testid={`area-row-${a.id}`}>
                      <td><div className="font-medium">{a.name}</div></td>
                      <td>{loc?.name || "—"}</td>
                      <td>{loc ? cliMap[loc.client_id] || "—" : "—"}</td>
                      <td>{a.area_type || "—"}</td>
                      <td><span className="text-sm">{epd}<span className="text-lx-muted">/día · {wdStr}</span></span></td>
                      <td>
                        <span className={`lx-badge ${a.status === "active" ? "lx-badge-success" : "lx-badge-muted"}`}>
                          {a.status === "active" ? "Activo" : "Inactivo"}
                        </span>
                      </td>
                      <td className="text-right whitespace-nowrap">
                        <button className="lx-btn lx-btn-ghost" onClick={() => showQR(a)} data-testid={`view-qr-${a.id}`} title="Ver QR"><QrCode size={14}/></button>
                        {!readOnly && <button className="lx-btn lx-btn-ghost" onClick={() => openEdit(a)} data-testid={`edit-area-${a.id}`}><Pencil size={14}/></button>}
                        {!readOnly && <button className="lx-btn lx-btn-ghost" onClick={() => remove(a.id)} data-testid={`delete-area-${a.id}`}><Trash2 size={14}/></button>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {modalOpen && (
        <Modal title={editing ? "Editar área" : "Nueva área"} onClose={close}>
          <form onSubmit={save} className="space-y-4">
            <div>
              <label className="lx-label">Sucursal <span className="text-lx-error">*</span></label>
              <select className="lx-input" value={form.location_id} onChange={(e)=>setForm({...form, location_id: e.target.value})} required data-testid="area-location-select">
                <option value="">— Seleccionar —</option>
                {locations.map(l => <option key={l.id} value={l.id}>{l.name} {cliMap[l.client_id] ? `· ${cliMap[l.client_id]}` : ""}</option>)}
              </select>
            </div>
            <Field label="Nombre del área" required value={form.name} onChange={(v)=>setForm({...form, name: v})} testid="area-name" placeholder="Ej. Baño mujeres planta baja"/>
            <div>
              <label className="lx-label">Tipo</label>
              <select className="lx-input" value={form.area_type} onChange={(e)=>setForm({...form, area_type: e.target.value})} data-testid="area-type-select">
                <option value="">—</option>
                {AREA_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
              <p className="text-xs text-lx-muted mt-1">Clasificación del área para agrupar reportes (opcional).</p>
            </div>
            <div className="rounded-lg border border-lx-border bg-lx-surface p-3 space-y-3">
              <div className="lx-overline">Frecuencia de limpieza</div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="lx-label">Checks esperados por día</label>
                  <input type="number" min="0" max="48" className="lx-input" value={form.expected_per_day} onChange={(e)=>setForm({...form, expected_per_day: parseInt(e.target.value)||0})} data-testid="area-expected-per-day"/>
                  <p className="text-xs text-lx-muted mt-1">Cuántas limpiezas se esperan cada día hábil.</p>
                </div>
                <div>
                  <label className="lx-label">Días hábiles</label>
                  <WeekdayPicker value={form.working_days} onChange={(v)=>setForm({...form, working_days: v})}/>
                  <p className="text-xs text-lx-muted mt-1">Días en los que aplica esta frecuencia.</p>
                </div>
              </div>
            </div>
            <div>
              <label className="lx-label">Instrucciones</label>
              <textarea className="lx-input" rows={3} value={form.instructions || ""} onChange={(e)=>setForm({...form, instructions: e.target.value})} data-testid="area-instructions"/>
            </div>
            <div className="flex items-center gap-2">
              <input id="photo_req" type="checkbox" checked={!!form.photo_required} onChange={(e)=>setForm({...form, photo_required: e.target.checked})} data-testid="area-photo-required"/>
              <label htmlFor="photo_req" className="text-sm">Foto de evidencia obligatoria</label>
            </div>
            <div>
              <label className="lx-label">Estatus</label>
              <select className="lx-input" value={form.status} onChange={(e)=>setForm({...form, status: e.target.value})} data-testid="area-status">
                <option value="active">Activo</option>
                <option value="inactive">Inactivo</option>
              </select>
            </div>
            {err && <div className="text-sm rounded-lg px-3 py-2 bg-red-50 text-red-700 border border-red-200">{err}</div>}
            <div className="flex justify-end gap-2 pt-2">
              <button type="button" className="lx-btn lx-btn-secondary" onClick={close}>Cancelar</button>
              <button type="submit" className="lx-btn lx-btn-primary" disabled={saving} data-testid="save-area-button">{saving ? "Guardando…" : "Guardar"}</button>
            </div>
          </form>
        </Modal>
      )}

      {qrModal && <QRModal area={qrModal} onClose={() => setQrModal(null)} />}
    </div>
  );
}

function QRModal({ area, onClose }) {
  const [data, setData] = useState(null);
  const [copied, setCopied] = useState(false);
  useEffect(() => {
    (async () => {
      const { data } = await api.get(`/areas/${area.id}/qr-data`);
      setData(data);
    })();
  }, [area.id]);

  const downloadPng = () => {
    const a = document.createElement("a");
    a.href = `${API_BASE}/areas/${area.id}/qr.png`;
    a.download = `qr-${area.name.replace(/\s+/g, "-")}.png`;
    a.target = "_blank";
    document.body.appendChild(a); a.click(); a.remove();
  };

  const printQR = () => {
    if (!data) return;
    const w = window.open("", "_blank");
    if (!w) return;
    w.document.write(`
      <html><head><title>QR · ${area.name}</title>
      <style>
        body{font-family:'Outfit',sans-serif;display:flex;align-items:center;justify-content:center;height:100vh;margin:0;background:#fff}
        .card{text-align:center;padding:32px;border:1px solid #E2E8F0;border-radius:12px;width:380px}
        h1{font-size:22px;margin:0 0 6px;letter-spacing:-0.02em}
        .sub{color:#475569;font-size:14px;margin-bottom:20px}
        img{width:300px;height:300px}
        .foot{margin-top:18px;font-size:11px;color:#94a3b8;letter-spacing:0.15em;text-transform:uppercase}
        .brand{color:#00AEEF;font-weight:700}
      </style></head><body><div class="card">
        <h1>${area.name}</h1>
        <div class="sub">Escanea para registrar la limpieza</div>
        <img src="${data.data_url}"/>
        <div class="foot"><span class="brand">Limpio Express</span> · QR Check System</div>
      </div><script>setTimeout(()=>window.print(),300)</script></body></html>`);
    w.document.close();
  };

  const copyUrl = async () => {
    if (!data) return;
    try { await navigator.clipboard.writeText(data.check_url); setCopied(true); setTimeout(()=>setCopied(false), 1500); } catch {}
  };

  return (
    <Modal title={`QR · ${area.name}`} onClose={onClose} maxWidth="max-w-md">
      <div className="text-center">
        {data ? (
          <>
            <img src={data.data_url} alt="QR" className="w-64 h-64 mx-auto rounded-lg border border-lx-border" data-testid="qr-image"/>
            <div className="mt-4 flex items-center justify-center gap-1.5 text-xs text-lx-muted">
              <ExternalLink size={12}/>
              <span className="truncate max-w-[280px]" data-testid="qr-url">{data.check_url}</span>
            </div>
            <div className="grid grid-cols-3 gap-2 mt-5">
              <button className="lx-btn lx-btn-secondary" onClick={copyUrl} data-testid="copy-qr-url">
                <Copy size={14}/> {copied ? "¡Copiado!" : "Copiar"}
              </button>
              <button className="lx-btn lx-btn-secondary" onClick={printQR} data-testid="print-qr">Imprimir</button>
              <button className="lx-btn lx-btn-primary" onClick={downloadPng} data-testid="download-qr">
                <Download size={14}/> PNG
              </button>
            </div>
          </>
        ) : (
          <div className="py-10 text-lx-muted">Generando QR…</div>
        )}
      </div>
    </Modal>
  );
}
